import {Alert, Button, Chip, Divider, Link, MenuItem, Paper, Select, Snackbar, Stack, Typography} from "@mui/material";
import React, {useCallback, useRef, useState} from "react";
import useApiCall, {makeApiCall} from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../../components/LoadingContent";
import {Link as RouterLink, useParams, useNavigate} from "react-router-dom";
import {EventResponse} from "../../data/events/EventResponse";
import {formatTimestamp, genericArrayItemUpdate} from "../../utils";
import {EventAttendeeResponse} from "../../data/events/EventAttendeeResponse";
import useUser, {UserContextProps} from "../../hooks/useUser";
import {EditorContent} from "../../components/editor/EditorContent";
import {LexicalEditor} from "lexical";
import {TextEditor} from "../../components/editor/TextEditor";
import {DetailedResourceResponse} from "../../data/DetailedResourceResponse";
import {$generateHtmlFromNodes} from '@lexical/html';
import {AlertType} from "../../data/AlertType";
import {EventRoleResponse} from "../../data/events/EventRoleResponse";
import InviteEventAttendeeForm from "../../components/event/InviteEventAttendeeForm";
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import EditEventForm from "../../components/event/EditEventForm";

interface EventCreationPageContentProps {
    event: EventResponse,
    user: UserContextProps
}

interface EventAttendeeProps {
    attendee: EventAttendeeResponse
    eventId: number
    eventRoles: EventRoleResponse[]
    modifyMasterListFun: React.Dispatch<React.SetStateAction<EventAttendeeResponse[]>>
    masterAttendeeList: EventAttendeeResponse[]
    canModifyRoles: boolean
}

function getCountOfAttendeesWithRole(attendees: EventAttendeeResponse[], roleIdentifier: string): number {
    let count = 0
    attendees.forEach((attendee) => {
        if (attendee.role?.identifier === roleIdentifier) { count += 1 }
    })
    return count
}

const EventAttendee = (props: EventAttendeeProps) => {
    const [status, setStatus] = useState(props.attendee.status)
    const [role, setRole] = useState(props.attendee.role)
    let statusChip
    let ignoreStatues: string[] = ["invited", "cancelled", "host"]

    switch (status) {
        case "attending":
            statusChip = <Chip size="small" label={"Attending"} color={"primary"} />
            break
        case "attended":
            statusChip = <Chip size="small" label={"Attended"} color={"success"} />
            break
        case "host":
            statusChip = <Chip size="small" label={"Host"} color={"secondary"} />
            break
        case "did_not_attend":
            statusChip = <Chip size="small" label={"Missed"} color={"error"} />
            break
        case "invited":
            statusChip = <Chip size="small" label={"Invited"} color={"warning"} />
            break
        case "cancelled":
            statusChip = <Chip size="small" label={"Cancelled"} color={"warning"} />
            break
        default:
            statusChip = <Chip size="small" label={"Unknown Status"} sx={{backgroundColor: "darkgray"}} />
    }

    return <Stack spacing={1} direction={"row"}>
        {props.canModifyRoles ? <Select disabled={
            ignoreStatues.includes(props.attendee.status)
        } value={status} onChange={(event) => {
            makeApiCall({
                url: `/api/event/status/${props.attendee.communityId}`,
                method: "PATCH",
                body: {
                    eventId: props.eventId,
                    newStatus: event.target.value
                },
                onLoadedCallback: () => {},
                onError: () => {}
            })
            setStatus(event.target.value)
        }} sx={{maxHeight: "30px", maxWidth: "250px"}}>
            <MenuItem key={"attending"} value={"attending"}>Attending</MenuItem>
            <MenuItem key={"host"} disabled value={"host"}>Host</MenuItem>
            <MenuItem key={"attended"} value={"attended"}>Attended</MenuItem>
            <MenuItem key={"did_not_attend"} value={"did_not_attend"}>Did Not Attend</MenuItem>
            <MenuItem key={"invited"} disabled value={"invited"}>Invited</MenuItem>
            <MenuItem key={"cancelled"} disabled value={"cancelled"}>Cancelled</MenuItem>
        </Select> : <></>}
        {!props.canModifyRoles ? <>
            <Typography>{role?.displayName ?? "Unassigned"} - <Link underline="none" component={RouterLink} to={`/user/${props.attendee.communityId}`}>
                {props.attendee.nick}
            </Link></Typography>
        </> : <>
            <Select value={role?.identifier ?? "none"} onChange={(event) => {
                makeApiCall({
                    url: `/api/event/role/${props.attendee.communityId}`,
                    method: "PATCH",
                    body: {
                        eventId: props.eventId,
                        roleIdentifier: event.target.value
                    },
                    onLoadedCallback: () => {
                        props.modifyMasterListFun(genericArrayItemUpdate(
                            props.masterAttendeeList,
                            "communityId",
                            props.attendee.communityId,
                            "role",
                            props.eventRoles.find((role) => { return role.identifier === event.target.value })
                        ))
                    },
                    onError: () => {}
                })
                setRole(props.eventRoles.find(role => role.identifier === event.target.value)!!)
            }} sx={{maxHeight: "30px", maxWidth: "450px"}}>
                {
                    props.eventRoles && props.eventRoles.map((role) => {
                        return <MenuItem disabled={getCountOfAttendeesWithRole(props.masterAttendeeList, role.identifier) >= (role.max ?? 99)}
                                         key={role.id}
                                         value={role.identifier
                        }>{role.displayName}</MenuItem>
                    })
                }
                    <MenuItem key={"none"} value={"none"}>Unassigned</MenuItem>
            </Select>
            <Link underline="none" component={RouterLink} to={`/user/${props.attendee.communityId}`}>
                {props.attendee.nick}
            </Link>
        </>}
        {statusChip}
    </Stack>
}

const EventViewingPageContent = (props: EventCreationPageContentProps) => {
    const user = useUser()
    const navigate = useNavigate()
    const editorRef = useRef<LexicalEditor>(null)
    const [showAlert, setShowAlert] = useState(false)
    const [alertType, setAlertType] = useState<AlertType>("success")
    const [alertText, setAlertText] = useState("")
    const setAlert = useCallback((type: AlertType, text: string) => {
        setAlertType(type)
        setAlertText(text)
        setShowAlert(true)
    }, [setAlertType, setAlertText, setShowAlert])
    const [masterAttendeeList, modifyMasterAttendeeList] = useState<EventAttendeeResponse[]>(props.event.eventAttendees)
    const [justAcceptedInviteAlert, setJustAcceptedInviteAlert] = useState(false)

    let eventUser = masterAttendeeList.find((attendee) => {
        return attendee.communityId === user.user?.communityId
    })

    let rejoinableEventAttendeeStatus: string[] = ["cancelled", "invited"]

    let userAttendee = props.event.eventAttendees.find(T => {
        return T.communityId === props.user.user?.communityId
    })

    let userRole = eventUser?.status === "host" ? "administrator" : eventUser?.role?.roleType ?? "non_attendee"
    let closedEventTypes: string[] = ["ended", "cancelled"]

        let attendanceButton = <>
            {
                userAttendee === undefined || rejoinableEventAttendeeStatus.includes(userAttendee.status) ? <>
                    <Button disabled={closedEventTypes.includes(props.event.status)}
                            size={"medium"} sx={{maxWidth: "100px", float: "right"}} color="info" variant="contained" onClick={() => {
                        makeApiCall({
                            url: `/api/event/own/status`,
                            method: "PATCH",
                            body: {
                                eventId: props.event.id,
                                newStatus: "ATTENDING"
                            },
                            onLoadedCallback: () => {
                                navigate(0)
                            },
                            onError: () => {}
                        })
                    }}>
                        Attend<EmojiPeopleIcon/>
                    </Button>
                </> : <>
                    <Button size={"medium"} disabled={
                        (props.event.host.communityId === props.user.user?.communityId) || (closedEventTypes.includes(props.event.status))
                    }
                            sx={{maxWidth: "160px", float: "right"}} color="warning" variant="contained" onClick={() => {
                        makeApiCall({
                            url: `/api/event/own/status`,
                            method: "PATCH",
                            body: {
                                eventId: props.event.id,
                                newStatus: "CANCELLED"
                            },
                            onLoadedCallback: () => {
                                navigate(0)
                            },
                            onError: () => {}
                        })
                    }}>
                        Withdraw<EmojiPeopleIcon/>
                    </Button>
                </>
            }
            </>

            let settingsButton = <>
                    {(props.event.host.communityId === props.user.user?.communityId || user.hasPermissions("events:admin")) && (!["started", "ended"].includes(props.event.status)) && (
                         <EditEventForm id={props.event.id}
                                        date={props.event.date}
                                        length={props.event.length}
                                        description={props.event.description}
                                        private={props.event.private}
                                        status={props.event.status}
                            />
                        )}
            </>

            let privateChip = <>
            <Chip size="small" label={props.event.private ? "Private" : "Public"} color={props.event.private ? "error" : "success"} sx={{ml: 0.5}}/>
            </>

    return <Stack spacing={2}>
        {userRole === "non_attendee" && eventUser !== undefined && !closedEventTypes.includes(props.event.status) ? <Alert severity="warning" variant={"outlined"}>You do not have a role assigned, as such you will not be able to join this event, or perform event functions, please contact the host or a hosting role to rectify this issue</Alert> : <></>}
        {eventUser?.status === "invited" ? <Alert severity="warning" variant={"outlined"}>You are currently listed as "invited" in order to be counted as present and perform event functions you need to accept your invite</Alert> : <></>}
        {eventUser?.status === "cancelled" ? <Alert severity="error" variant={"outlined"}>You have accepted, and then declined this event, please notify the host to avoid any confusion and or punishment per divisional guidelines</Alert> : <></>}
        {justAcceptedInviteAlert ? <Alert severity="success" variant={"outlined"}>You have accepted your invite to this event! If you have any warnings please refresh this page, if they persists please do what the warnings recommend</Alert> : <></>}
        <Paper variant={"outlined"} style={{
            padding: "16px"
        }}>
            <Stack spacing={2}>
                <Typography variant={"h5"}>{props.event.type.displayName} - {formatTimestamp(props.event.date)} ({props.event.status[0].toUpperCase() + props.event.status.slice(1)}) {privateChip} {settingsButton} {attendanceButton}</Typography>
                <Divider />
                <Typography>Host - <Link underline="none" component={RouterLink} to={`/user/${props.event.host.communityId}`}>
                    {props.event.host.nick}
                </Link></Typography>
                <Typography>Description - {props.event.description}</Typography>
                <Divider />
                <Stack direction={"row"} spacing={2}>
                    <Typography variant={"h6"}>Attendees</Typography>
                    {
                        userRole === "administrator" && !closedEventTypes.includes(props.event.status) ? <InviteEventAttendeeForm
                            event={props.event}
                            eventAttendees={masterAttendeeList}
                            onSuccessfulInvite={(nick: string, communityId: string) => {
                                modifyMasterAttendeeList([...masterAttendeeList, {
                                    id: -1,
                                    communityId: communityId,
                                    nick: nick,
                                    role: undefined,
                                    status: "invited"
                                }])
                            }}
                        /> : <></>
                    }
                </Stack>
                {masterAttendeeList.map((attendee, idx) => {
                    return <EventAttendee
                        eventId={props.event.id}
                        attendee={attendee}
                        key={idx}
                        canModifyRoles={userRole === "administrator" && !closedEventTypes.includes(props.event.status)}
                        eventRoles={props.event.roles}
                        modifyMasterListFun={modifyMasterAttendeeList}
                        masterAttendeeList={masterAttendeeList}
                    />
                })}
                {
                    userRole === "administrator" && !closedEventTypes.includes(props.event.status) ?
                        <Stack spacing={1} direction={"row"}>
                            {props.event.roles && props.event.roles.map((role, idx) => {
                                return <Typography key={idx} variant={"caption"} fontStyle={"italic"} color={"gray"}>{role.displayName}
                                    ({getCountOfAttendeesWithRole(masterAttendeeList, role.identifier)}/{role.max ?? "Unlimited"})
                                </Typography>
                            })}
                        </Stack> : <></>
                }
                <Divider />
                <Typography variant={"h6"}>Event Note</Typography>
                {
                    userRole === "administrator" ? <>
                            <TextEditor ref={editorRef} initialContent={props.event.note ?? ""}/>
                            <Button variant={"contained"} color="info" onClick={() => {
                                if (editorRef.current) {
                                    editorRef.current.getEditorState().read(() => {
                                        makeApiCall<DetailedResourceResponse>({
                                            url: `/api/event/note/${props.event.id}`,
                                            method: "POST",
                                            body: {
                                                newNote: $generateHtmlFromNodes(editorRef.current!)
                                            },
                                            onLoadedCallback: () => {
                                                setAlert("success", "Note Saved")
                                            },
                                            onError: () => {
                                                setAlert("error", "Failed to save note")
                                            }
                                        })
                                    })
                                }
                            }}>Save Note</Button>
                        </> :
                        <>
                            {
                                props.event.note === null ? <Typography sx={{ fontStyle: 'oblique' }}>No note has been entered yet
                                </Typography> : <EditorContent content={props.event.note ?? ""} />
                            }
                        </>
                }
                {
                    eventUser?.status === "invited" ? <>
                            <Button variant={"contained"} color="success" onClick={() => {
                                makeApiCall<DetailedResourceResponse>({
                                    url: `/api/event/own/status`,
                                    method: "PATCH",
                                    body: {
                                        eventId: props.event.id,
                                        newStatus: "ATTENDING"
                                    },
                                    onLoadedCallback: () => {
                                        genericArrayItemUpdate(
                                            masterAttendeeList,
                                            "communityId",
                                            eventUser?.communityId,
                                            "status",
                                            "attending"
                                        )
                                        setJustAcceptedInviteAlert(true)
                                        setAlert("success", "Accepted Invite")
                                    },
                                    onError: () => {}
                                })
                            }}>Accept Invite</Button>
                        </> : <> </>
                }
            </Stack>
            <Snackbar open={showAlert} autoHideDuration={5000} onClose={() => setShowAlert(false)}>
                <Alert severity={alertType} onClose={() => setShowAlert(false)} sx={{width: "100%"}} variant="filled">
                    {alertText}
                </Alert>
            </Snackbar>
        </Paper>
    </Stack>
}

export const EventViewingPage = () => {
    const params = useParams()
    const loadEvent = useApiCall<EventResponse>({
        initialUrl: `/api/event/details/${params.id}`
    })

const user = useUser()

    return <LoadingContent isLoading={
        loadEvent.isLoading || !loadEvent.data
    }>
        <EventViewingPageContent event={loadEvent.data!!} user={user} />
    </LoadingContent>
}